import { useEffect, useMemo, useState } from "react";
import Button from "@material-ui/core/Button";
import Grid from "@material-ui/core/Grid";
import DetailSection from "../../../../components/DetailSection";
import {
  featureCategories,
  featureDescription,
  featureRequirements,
  oneOfSettings,
} from "../../constants";
import {
  FormGroup,
  Typography,
  Tooltip,
  Paper,
  IconButton,
  TextField,
  InputAdornment,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core";
import CheckCircleIcon from "@material-ui/icons/CheckCircle";
import EditIcon from "@material-ui/icons/Edit";
import SearchIcon from "@material-ui/icons/Search";
import ClearIcon from "@material-ui/icons/Clear";
import { FeatureChangesModal } from "./components/FeatureChangesModal";
import { FeatureCheckbox } from "./components/FeatureCheckbox";
import { OneOf } from "./components/OneOf";

const useStyles = makeStyles((theme) => ({
  featureActions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(2),
    marginTop: theme.spacing(2),
  },
  titleContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
  },
  editButton: {
    minWidth: "unset",
    padding: theme.spacing(0.5),
  },
  searchField: {
    marginBottom: theme.spacing(2),
  },
  categoryCard: {
    padding: 0,
    height: "100%",
    display: "flex",
    flexDirection: "column",
  },
  categoryHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: theme.spacing(1.5, 2),
    backgroundColor: theme.palette.grey[50],
  },
  categoryTitle: {
    fontWeight: 600,
  },
  featureList: {
    padding: 0,
    flex: 1,
  },
  featureListItem: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  featureListItemEnabled: {
    paddingTop: theme.spacing(0.5),
    paddingBottom: theme.spacing(0.5),
  },
  enabledIcon: {
    fontSize: 18,
    color: theme.palette.success.main,
  },
  listItemIcon: {
    minWidth: 32,
  },
  featureName: {
    fontWeight: 500,
    fontSize: "0.875rem",
  },
  featureDesc: {
    fontSize: "0.75rem",
    color: theme.palette.text.secondary,
    marginTop: 1,
  },
}));

/**
 * Groups features within a category into parent/child pairs.
 * A feature is a "child" if its requirements reference another feature
 * in the same category list. Returns a flat array of { feature, indent }
 * with children placed right after their parent.
 */
const groupFeatures = (featureList) => {
  const set = new Set(featureList);
  const childOf = {};

  featureList.forEach((f) => {
    const req = featureRequirements[f];
    if (!req) return;
    const parents = req.or || req.and || [];
    const parentInCategory = parents.find((p) => set.has(p));
    if (parentInCategory) childOf[f] = parentInCategory;
  });

  const children = new Set(Object.keys(childOf));
  const result = [];

  const addWithChildren = (f, depth) => {
    result.push({ feature: f, indent: depth });
    featureList.forEach((c) => {
      if (childOf[c] === f) addWithChildren(c, depth + 1);
    });
  };

  featureList.forEach((f) => {
    if (children.has(f)) return;
    addWithChildren(f, 0);
  });

  return result;
};

const featureMatchesSearch = (feature, query) => {
  const q = query.toLowerCase();
  return (
    feature.toLowerCase().includes(q) ||
    (featureDescription[feature] || "").toLowerCase().includes(q)
  );
};

const getOneOfSettingsForParent = (parentFeature) => {
  return Object.entries(oneOfSettings).filter(([, config]) => {
    return config.or?.includes(parentFeature);
  });
};

export default function Features({ partner, onSave }) {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [showChanges, setShowChanges] = useState(false);
  const [features, setFeatures] = useState(partner?.features ?? []);
  const [search, setSearch] = useState("");

  useEffect(() => {
    setFeatures(partner?.features ?? []);
  }, [partner]);

  const categoryData = useMemo(() => {
    const partnerFeatures = partner?.features ?? [];
    return Object.entries(featureCategories).map(([category, categoryFeatures]) => {
      const filtered = search
        ? categoryFeatures.filter((f) => featureMatchesSearch(f, search))
        : categoryFeatures;
      const enabledCount = categoryFeatures.filter((f) =>
        isEditing ? features.includes(f) : partnerFeatures.includes(f)
      ).length;
      return { category, allFeatures: categoryFeatures, filtered, enabledCount };
    });
  }, [search, features, partner, isEditing]);

  const hasChanges =
    features.length !== (partner?.features ?? []).length ||
    !features.every((f) => (partner?.features ?? []).includes(f));

  const editButton = (
    <Tooltip title="Edit features">
      <span>
        <IconButton
          className={classes.editButton}
          size="small"
          onClick={() => setIsEditing(true)}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      </span>
    </Tooltip>
  );

  const titleWithEdit = (
    <div className={classes.titleContainer}>
      Features
      {!isEditing && editButton}
      {isEditing && (
        <Button
          variant="text"
          size="small"
          onClick={() => {
            setFeatures(partner?.features ?? []);
            setIsEditing(false);
          }}
        >
          Cancel changes
        </Button>
      )}
    </div>
  );

  const searchBar = (
    <TextField
      className={classes.searchField}
      variant="outlined"
      size="small"
      fullWidth
      placeholder="Search features..."
      value={search}
      onChange={(e) => setSearch(e.target.value)}
      InputProps={{
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon color="action" />
          </InputAdornment>
        ),
        endAdornment: search ? (
          <InputAdornment position="end">
            <IconButton size="small" onClick={() => setSearch("")}>
              <ClearIcon fontSize="small" />
            </IconButton>
          </InputAdornment>
        ) : null,
      }}
    />
  );

  if (!isEditing) {
    return (
      <DetailSection title={titleWithEdit} mt={0}>
        {searchBar}
        <Grid container spacing={2}>
          {categoryData.map(({ category, allFeatures, filtered, enabledCount }) => {
            const enabledFiltered = filtered.filter((f) =>
              (partner?.features ?? []).includes(f)
            );
            if (enabledFiltered.length === 0) return null;
            return (
              <Grid item xs={12} md={6} key={category}>
                <Paper className={classes.categoryCard} variant="outlined">
                  <div className={classes.categoryHeader}>
                    <Typography variant="subtitle2" className={classes.categoryTitle}>
                      {category}
                    </Typography>
                    <Chip
                      label={`${enabledCount}/${allFeatures.length}`}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  </div>
                  <Divider />
                  <List className={classes.featureList} dense>
                    {groupFeatures(enabledFiltered).map(({ feature, indent }) => (
                      <ListItem
                        key={feature}
                        className={classes.featureListItemEnabled}
                        style={indent ? { paddingLeft: 40 * Math.min(indent, 2) } : undefined}
                      >
                        <ListItemIcon className={classes.listItemIcon}>
                          <CheckCircleIcon className={classes.enabledIcon} />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <div className={classes.featureName}>{feature}</div>
                          }
                          secondary={
                            <div className={classes.featureDesc}>
                              {featureDescription[feature]}
                            </div>
                          }
                          disableTypography
                        />
                      </ListItem>
                    ))}
                  </List>
                  {enabledFiltered.flatMap((feature) =>
                    getOneOfSettingsForParent(feature).map(([key, config]) => (
                      <OneOf
                        key={key}
                        title={config.title}
                        options={config.options}
                        defaultValue={config.defaultValue}
                        features={partner?.features ?? []}
                        setFeatures={() => {}}
                        disabled
                      />
                    ))
                  )}
                </Paper>
              </Grid>
            );
          })}
        </Grid>
      </DetailSection>
    );
  }

  return (
    <>
      <DetailSection title={titleWithEdit} mt={0}>
        {searchBar}
        <FormGroup>
          <Grid container spacing={2}>
            {categoryData.map(({ category, allFeatures, filtered }) => {
              if (filtered.length === 0) return null;
              return (
                <Grid item xs={12} md={6} key={category}>
                  <Paper className={classes.categoryCard} variant="outlined">
                    <div className={classes.categoryHeader}>
                      <Typography variant="subtitle2" className={classes.categoryTitle}>
                        {category}
                      </Typography>
                      <Chip
                        label={`${
                          allFeatures.filter((f) => features.includes(f)).length
                        }/${allFeatures.length}`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </div>
                    <Divider />
                    <List className={classes.featureList} dense>
                      {groupFeatures(filtered).map(({ feature, indent }) => (
                        <FeatureCheckbox
                          key={feature}
                          feature={feature}
                          features={features}
                          setFeatures={setFeatures}
                          partner={partner}
                          compact
                          indent={indent}
                        />
                      ))}
                    </List>
                    {filtered
                      .filter((feature) => features.includes(feature))
                      .flatMap((feature) =>
                        getOneOfSettingsForParent(feature).map(([key, config]) => (
                          <OneOf
                            key={key}
                            title={config.title}
                            options={config.options}
                            defaultValue={config.defaultValue}
                            features={features}
                            setFeatures={setFeatures}
                          />
                        ))
                      )}
                  </Paper>
                </Grid>
              );
            })}
          </Grid>

          <div className={classes.featureActions}>
            <Button
              variant="outlined"
              color="secondary"
              disabled={!hasChanges}
              onClick={() => setFeatures(partner?.features ?? [])}
            >
              Revert Changes
            </Button>

            <Button
              variant="contained"
              color="primary"
              disabled={!hasChanges}
              onClick={() => setShowChanges(true)}
            >
              Apply Changes
            </Button>
          </div>
        </FormGroup>
      </DetailSection>

      <FeatureChangesModal
        open={showChanges}
        onClose={() => setShowChanges(false)}
        onConfirm={() => {
          onSave(features);
          setShowChanges(false);
          setIsEditing(false);
        }}
        currentFeatures={partner?.features ?? []}
        newFeatures={features}
      />
    </>
  );
}
